import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Camera, Upload, Sparkles, Loader2, X, ImagePlus } from "lucide-react";

/**
 * BusinessCardScanDialog — take/upload a photo of a business card, hit the
 * `/business-card/scan` endpoint (GPT-5.4 vision), and pipe the extracted
 * fields into whatever form is on the page via `onScanned(fields)`.
 *
 * The consumer decides how to merge — usually by pre-populating an empty
 * form so the user can edit before hitting Create Business.
 */

// Client-side max — matches server's 5 MB cap.
const MAX_BYTES = 5 * 1024 * 1024;

// Convert a File → data-url base64 for POSTing. Kept inline because we only
// use it here and it keeps the component self-contained.
function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function BusinessCardScanDialog({ open, onOpenChange, onScanned, triggerLabel }) {
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [scanning, setScanning] = useState(false);

  const reset = () => {
    setPreview(null);
    setScanning(false);
  };
  const close = () => { reset(); onOpenChange(false); };

  const handleFile = async (file) => {
    if (!file) return;
    if (!/^image\/(jpeg|png|webp|heic|heif)$/i.test(file.type) && !/\.(jpe?g|png|webp|heic|heif)$/i.test(file.name)) {
      toast.error("Please upload a JPG, PNG, or WEBP image.");
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error(`Image is too big (${Math.round(file.size / 1024 / 1024)} MB). Max 5 MB.`);
      return;
    }
    try {
      const dataUrl = await fileToDataUrl(file);
      setPreview(dataUrl);
    } catch {
      toast.error("Couldn't read that image.");
    }
  };

  const runScan = async () => {
    if (!preview) return;
    setScanning(true);
    try {
      const res = await axios.post(
        `${API}/business-card/scan`,
        { image_base64: preview },
        {
          withCredentials: true,
          timeout: 60000,
        },
      );
      const data = res.data || {};
      const conf = data.confidence || "medium";
      const anyField = data.name || data.email || data.phone || data.website;
      if (!anyField) {
        toast.error(
          "Couldn't read that card. Try better lighting or a straight-on shot.",
        );
        setScanning(false);
        return;
      }
      onScanned?.(data);
      toast.success(
        conf === "low"
          ? "Extracted a few fields — please double-check them."
          : "Card scanned — review and submit.",
      );
      close();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Scan failed. Try again.");
      setScanning(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DialogContent className="max-w-md" data-testid="business-card-scan-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-tbc-navy">
            <Sparkles className="w-5 h-5 text-tbc-gold" />
            {triggerLabel || "Scan a business card"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {!preview ? (
            <>
              <p className="text-sm text-gray-600">
                Take a photo (or upload one) of a business card and we&apos;ll
                auto-fill the registration form for you. You can review and
                edit every field before submitting.
              </p>

              <div className="grid grid-cols-2 gap-3">
                <Button
                  type="button"
                  onClick={() => cameraRef.current?.click()}
                  className="h-auto py-6 flex flex-col items-center gap-2 bg-tbc-navy text-white hover:bg-tbc-navy/90"
                  data-testid="scan-take-photo-btn"
                >
                  <Camera className="w-6 h-6" />
                  Take photo
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileRef.current?.click()}
                  className="h-auto py-6 flex flex-col items-center gap-2 border-tbc-navy/30 text-tbc-navy"
                  data-testid="scan-upload-btn"
                >
                  <Upload className="w-6 h-6" />
                  Upload photo
                </Button>
              </div>

              {/* Hidden file inputs. `capture="environment"` on the camera one
                  makes iOS/Android open the rear-facing camera directly. */}
              <input
                ref={cameraRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])}
                data-testid="scan-camera-input"
              />
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])}
                data-testid="scan-file-input"
              />

              <div className="border-t border-gray-200 pt-3">
                <p className="text-xs text-gray-500">
                  <strong>Tips for a clean scan:</strong> shoot straight-on,
                  fill the frame with the card, use daylight or a bright lamp,
                  avoid glare on the printed text.
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="relative rounded-lg overflow-hidden bg-gray-100 border border-gray-200">
                <img
                  src={preview}
                  alt="Business card preview"
                  className="w-full max-h-72 object-contain"
                  data-testid="scan-preview-img"
                />
                <button
                  type="button"
                  onClick={reset}
                  disabled={scanning}
                  className="absolute top-2 right-2 p-1.5 rounded-full bg-black/60 hover:bg-black/80 text-white"
                  aria-label="Replace image"
                  data-testid="scan-replace-btn"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <p className="text-xs text-gray-500 text-center">
                Looks good? Extract the fields — you&apos;ll review before submitting.
              </p>
            </>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={close} disabled={scanning} data-testid="scan-cancel-btn">
            Cancel
          </Button>
          {preview && (
            <Button
              type="button"
              onClick={runScan}
              disabled={scanning}
              className="bg-tbc-gold text-tbc-navy hover:bg-tbc-gold/90 font-semibold"
              data-testid="scan-extract-btn"
            >
              {scanning ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Reading card…</>
              ) : (
                <><ImagePlus className="w-4 h-4 mr-2" /> Extract fields</>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
